import type { DiscoverySummary, MatrixResult, TerritoryPoint } from '../lib/api'
import { topPoints } from '../lib/matrixSpec'

interface Props {
  result: MatrixResult
  discovery: DiscoverySummary | null
}

const QUADRANT_LABELS: Record<string, string> = {
  territorio_branco: 'Territórios brancos',
  campo_de_batalha: 'Campos de batalha',
  fortaleza: 'Fortalezas',
  descarte: 'Descartes',
}

export function AnalysisHeader({ result }: Pick<Props, 'result'>) {
  const { brief, provenance } = result

  return (
    <header className="report-head">
      <div className="report-title">
        <p className="eyebrow">Matriz de ativação / {kpiLabel(brief.objective.kpi)}</p>
        <h1>{brief.label ?? 'Leitura estratégica de territórios'}</h1>
        <p className="report-subtitle">
          <strong>{brief.audience.label}</strong>
          <span>para {displayBrand(brief.brands.focus)}</span>
        </p>
      </div>
      <dl className="report-meta">
        <div><dt>Mercado</dt><dd>{brief.audience.market}</dd></div>
        <div><dt>Wave</dt><dd>{provenance.wave ?? '—'}</dd></div>
        <div><dt>Horizonte</dt><dd>{brief.objective.horizon_days} dias</dd></div>
        <div><dt>Concorrentes</dt><dd>{brief.brands.competitors.length}</dd></div>
      </dl>
    </header>
  )
}

/**
 * Depois do gráfico e do radar, não no topo: com peso por dimensão, o primeiro do ranking
 * bruto pode ser justamente o que o planejador vai rebaixar. Lê o mesmo `topPoints` do
 * ranking, então acompanha cada peso aplicado.
 */
export function PrioritySignal({ result }: Pick<Props, 'result'>) {
  const { brief } = result
  const topPoint: TerritoryPoint | null = topPoints(result.points, 1)[0] ?? null
  const weighted = topPoint?.adjusted_priority_score != null
  const quadrants = result.points.reduce<Record<string, number>>((acc, point) => {
    acc[point.quadrant] = (acc[point.quadrant] ?? 0) + 1
    return acc
  }, {})

  return (
    <section className="decision-summary" aria-label="Resumo executivo">
      <div className="decision-lead">
        <p className="eyebrow">Sinal de prioridade</p>
        <h2>
          {topPoint ? (
            <>
              A maior prioridade{weighted ? ', com os ajustes aplicados,' : ' calculada'} é{' '}
              <em>{topPoint.label}</em>.
            </>
          ) : (
            <>A matriz não contém territórios elegíveis.</>
          )}
        </h2>
        {topPoint && (
          <p>
            Afinidade {topPoint.affinity_index.toFixed(0)}, alcance de {topPoint.reach_pct.toFixed(1)}%
            {' '}e afinidade competitiva {topPoint.competitive_saturation.toFixed(0)}. O ranking responde ao KPI
            {' '}<strong>{kpiLabel(brief.objective.kpi)}</strong> — não é uma ordenação genérica.
          </p>
        )}
      </div>
      <div className="quadrant-index">
        {Object.entries(QUADRANT_LABELS).map(([key, label], index) => (
          <div key={key}>
            <span className="quadrant-number">{String(index + 1).padStart(2, '0')}</span>
            <strong>{quadrants[key] ?? 0}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

/** Notas de método: ficam no fim da página, depois da leitura, como apêndice auditável. */
export function MethodNotes({ result, discovery }: Props) {
  const byKind = result.suppressed.reduce<Record<string, number>>((acc, item) => {
    acc[item.ref.kind] = (acc[item.ref.kind] ?? 0) + 1
    return acc
  }, {})

  return (
    <section className="method-notes">
      {discovery && (
        <details className="method-note">
          <summary>
            <span className="note-symbol">i</span>
            <span>
              <strong>Por que idade, gênero e renda não aparecem como territórios?</strong>
              <small>A ausência é deliberada — abra para entender o recorte.</small>
            </span>
            <span className="summary-toggle" aria-hidden="true">+</span>
          </summary>
          <div className="note-body">
            <p>{discovery.ui_note}</p>
            {discovery.n_variables_scanned > 0 ? (
              <dl className="discovery-stats">
                <div><dt>Variáveis varridas</dt><dd>{discovery.n_variables_scanned}</dd></div>
                <div><dt>Candidatos testados</dt><dd>{discovery.n_candidates_tested}</dd></div>
                <div><dt>Rejeitados pelo FDR</dt><dd>{discovery.n_rejected_by_fdr}</dd></div>
                <div><dt>Fora do corte visual</dt><dd>{discovery.n_truncated}</dd></div>
              </dl>
            ) : (
              <p className="note-detail">
                Modo demo · dicionário fixo de territórios · nenhuma consulta externa.
              </p>
            )}
            {discovery.audience_variables_excluded.length > 0 && (
              <p className="note-detail">
                Variáveis que definem este recorte: {discovery.audience_variables_excluded.join(', ')}.
                Elas podem voltar a ser achado quando a audiência for outra.
              </p>
            )}
          </div>
        </details>
      )}

      {result.suppressed.length > 0 && (
        <details className="method-note warning-note">
          <summary>
            <span className="note-symbol">!</span>
            <span>
              <strong>{result.suppressed.length} limitações estatísticas registradas</strong>
              <small>
                {byKind.territory ? `${byKind.territory} território(s) fora · ` : ''}
                {byKind.competitor ? `${byKind.competitor} eixo(s) Y frágil(is) · ` : ''}
                {byKind.brand ? `${byKind.brand} marca(s) sem base` : ''}
              </small>
            </span>
            <span className="summary-toggle" aria-hidden="true">+</span>
          </summary>
          <div className="note-body">
            <p>
              Dado frágil continua visível, com a limitação junto dele. Ausência de base não é
              ausência de oportunidade — e por isso cada caso permanece auditável.
            </p>
            <ul className="suppressed-list">
              {result.suppressed.map((item) => (
                <li key={`${item.ref.kind}:${item.ref.key}:${item.ref.field}`}>
                  <span>{item.ref.kind}</span>
                  <div><strong>{item.label}</strong><p>{item.reason}</p></div>
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}
    </section>
  )
}

function displayBrand(value: string): string {
  return value.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

function kpiLabel(value: string): string {
  const labels: Record<string, string> = {
    awareness: 'Awareness', consideration: 'Consideração', intent: 'Intenção',
    conversion: 'Conversão', loyalty: 'Lealdade',
  }
  return labels[value] ?? value
}
